import { useMemo } from 'react';
import { getAccessToken } from '@nexus/supabase';

import type { ConnectorRecord, ConnectorTokenStatus } from '@nexus/ari-lib';
import {
  useConnectors as useConnectorsBase,
  CONNECTORS_QUERY_KEY,
  CONNECTION_STATUS_QUERY_KEY,
  initiateConnection as initiateConnectionBase,
  disconnectProvider as disconnectProviderBase,
} from '@nexus/ari-lib';

import type { UseConnectorsApi, DiscoverMcpParams, DiscoverMcpResult, ConnectionResult } from '@nexus/ari-lib';

import { supabaseEdgeFetch } from '../lib/supabase-edge';
import { getAuthBackendUrl } from '../config/global';

export type { ConnectorRecord, ConnectionResult };
export { CONNECTORS_QUERY_KEY, CONNECTION_STATUS_QUERY_KEY };

// ---------------------------------------------------------------------------
// API client — Supabase Edge calls go through supabaseEdgeFetch (lib factory).
// The /auth/connectors/* endpoints still use a direct fetch + Bearer header
// because in local dev they get routed through the Vite proxy (see
// getAuthBackendUrl), while the Supabase Edge calls always hit the URL
// directly.
// ---------------------------------------------------------------------------

interface ConnectorsListResponse {
  connectors: ConnectorRecord[];
  user_enabled_connector_ids: string[];
  user_connector_statuses?: Record<
    string,
    {
      token_status: ConnectorTokenStatus;
      token_status_at: string | null;
      token_status_error: string | null;
    }
  >;
}

interface ConnectorConnectionStatus {
  connector_id: string;
  token_status: ConnectorTokenStatus;
  token_status_at: string | null;
  token_status_error: string | null;
}

async function getToken(): Promise<string> {
  const token = await getAccessToken();
  if (!token) throw new Error('No authenticated session');
  return token;
}

// Exported so route loaders can prefetch via queryClient.ensureQueryData.
export async function fetchConnectors(): Promise<ConnectorsListResponse> {
  return supabaseEdgeFetch<ConnectorsListResponse>('connectors');
}

async function enableUserConnector(connectorId: string) {
  return supabaseEdgeFetch(`connectors/me/${connectorId}`, { method: 'POST' });
}

async function disableUserConnector(connectorId: string) {
  return supabaseEdgeFetch(`connectors/me/${connectorId}`, { method: 'DELETE' });
}

async function getConnectionStatus(): Promise<ConnectorConnectionStatus[]> {
  const token = await getToken();
  const baseUrl = getAuthBackendUrl();
  const response = await fetch(`${baseUrl}/auth/connectors`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error('Failed to get connection status');
  const data: { statuses: ConnectorConnectionStatus[] } = await response.json();
  return data.statuses;
}

async function refreshConnectorStatus() {
  const token = await getToken();
  const baseUrl = getAuthBackendUrl();
  const response = await fetch(`${baseUrl}/auth/connectors/refresh`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    throw new Error('Failed to refresh connector status');
  }
  return response.json();
}

// ---------------------------------------------------------------------------
// OAuth connection helpers (thin wrappers over shared ari-lib functions)
// ---------------------------------------------------------------------------

async function authFetch(url: string, init?: RequestInit): Promise<Response> {
  const token = await getToken();
  return fetch(url, {
    ...init,
    headers: { ...init?.headers, Authorization: `Bearer ${token}` },
  });
}

async function initiateConnection(connectorId: string): Promise<ConnectionResult> {
  return initiateConnectionBase(authFetch, getAuthBackendUrl(), connectorId);
}

async function disconnectProvider(connectorId: string): Promise<boolean> {
  return disconnectProviderBase(authFetch, getAuthBackendUrl(), connectorId);
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

async function deleteConnector(connectorId: string) {
  return supabaseEdgeFetch(`connectors/${connectorId}`, { method: 'DELETE' });
}

async function discoverMcp(params: DiscoverMcpParams): Promise<DiscoverMcpResult> {
  return supabaseEdgeFetch<DiscoverMcpResult>('mcp/discover', { method: 'POST', body: params });
}

const api: UseConnectorsApi = {
  fetchConnectors,
  enableUserConnector,
  disableUserConnector,
  deleteConnector,
  discoverMcp,
  getConnectionStatus,
  initiateConnection,
  disconnectProvider,
  refreshConnectorStatus,
};

export function useConnectors() {
  return useConnectorsBase(useMemo(() => api, []));
}
